import { useCallback } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import Sentry from "@similarweb/sentry-logger";

import { useToasts } from "app/common/hooks";
import { useApiMutation } from "app/modules/api-client";
import type { InvalidateQueriesData } from "app/modules/api-client/utils";
import { getResponseErrorDetail } from "app/services/fetchService";

type UseSecondaryDomainsMutationParams = {
    activationId: string;
    hasExistingData: boolean;
};

export const useSecondaryDomainsMutation = ({ activationId, hasExistingData }: UseSecondaryDomainsMutationParams) => {
    const { showErrorToast } = useToasts();
    const { translate } = useTranslate();
    const invalidateQueries = {
        Alerts: { userdataActivationAlertsSecondaryDomainsDetail: [activationId] },
    } satisfies InvalidateQueriesData;

    const { mutateAsync: createSecondaryDomains, isPending: isCreatePending } = useApiMutation(
        "Alerts",
        "userdataActivationAlertsSecondaryDomainsCreate",
        {
            invalidateQueries,
            onError: (error, variables) => {
                Sentry.errorTracker({
                    e: error,
                    message: "Error in createSecondaryDomains mutation",
                    level: "error",
                    params: {
                        extra: {
                            hasExistingData,
                            variables,
                        },
                    },
                });

                showErrorToast(translate("common.api.error", { error: getResponseErrorDetail(error) }));
            },
        },
    );

    const { mutateAsync: updateSecondaryDomains, isPending: isUpdatePending } = useApiMutation(
        "Alerts",
        "userdataActivationAlertsSecondaryDomainsUpdate",
        {
            invalidateQueries,
            onError: (error, variables) => {
                Sentry.errorTracker({
                    e: error,
                    message: "Error in updateSecondaryDomains mutation",
                    level: "error",
                    params: {
                        extra: {
                            hasExistingData,
                            variables,
                        },
                    },
                });

                showErrorToast(translate("common.api.error", { error: getResponseErrorDetail(error) }));
            },
        },
    );

    const { mutateAsync: deleteSecondaryDomains, isPending: isDeletePending } = useApiMutation(
        "Alerts",
        "userdataActivationAlertsSecondaryDomainsDelete",
        {
            invalidateQueries,
            onError: (error, variables) => {
                Sentry.errorTracker({
                    e: error,
                    message: "Error in deleteSecondaryDomains mutation",
                    level: "error",
                    params: {
                        extra: {
                            hasExistingData,
                            variables,
                        },
                    },
                });

                showErrorToast(translate("common.api.error", { error: getResponseErrorDetail(error) }));
            },
        },
    );

    const mutateAsync = useCallback(
        async (domains: string[]) => {
            const hasNewData = domains.length > 0;

            if (!hasExistingData && hasNewData) {
                await createSecondaryDomains([activationId, { domains }]);
                return;
            }

            if (hasExistingData && hasNewData) {
                await updateSecondaryDomains([activationId, { domains }]);
                return;
            }

            if (hasExistingData && !hasNewData) {
                await deleteSecondaryDomains([activationId]);
            }
        },
        [activationId, hasExistingData, createSecondaryDomains, updateSecondaryDomains, deleteSecondaryDomains],
    );

    return {
        mutateAsync,
        isPending: isCreatePending || isUpdatePending || isDeletePending,
    };
};
